import {
  FolderKanban, Users, Clock, AlertCircle, Layers,
  DollarSign, BarChart2, CalendarOff, AlertTriangle, UserX, Timer,
} from "lucide-react";
import { Link } from "react-router-dom";
import { registerWidget } from "@/modules/shared/dashboard/registry";
import { KpiWidget } from "@/modules/shared/dashboard/widgets/shells/KpiWidget";
import { ListWidget } from "@/modules/shared/dashboard/widgets/shells/ListWidget";
import { HoursTrendChart } from "./shells/HoursTrendChart";
import { AllocationsByStatusChart } from "./shells/AllocationsByStatusChart";
import { fmtBRL, fmtPct, fmtHours } from "@/modules/shared/dashboard/formatters";

// ── KPI registrations ────────────────────────────────────────────────────────

registerWidget({
  id: "operations.activeProjects", module: "operations", title: "Projetos ativos",
  description: "Total de projetos em andamento neste momento.", size: "1x1", category: "kpi", icon: FolderKanban,
  Component: () => (
    <KpiWidget tone="ink" title="Projetos ativos" endpoint="/operations/dashboard/active-projects"
               icon={FolderKanban} href="/projetos" />
  ),
});

registerWidget({
  id: "operations.activeAllocations", module: "operations", title: "Alocações ativas",
  description: "Total de profissionais com alocação vigente.", size: "1x1", category: "kpi", icon: Users,
  Component: () => (
    <KpiWidget tone="blue" title="Alocações ativas" endpoint="/operations/dashboard/active-allocations"
               icon={Users} href="/alocacoes" />
  ),
});

registerWidget({
  id: "operations.hoursThisMonth", module: "operations", title: "Horas no mês",
  description: "Horas aprovadas ou submetidas no mês corrente.", size: "1x1", category: "kpi", icon: Clock,
  Component: () => (
    <KpiWidget tone="green" title="Horas no mês" endpoint="/operations/dashboard/hours-this-month"
               icon={Clock} href="/timesheet" valueFormatter={fmtHours} />
  ),
});

registerWidget({
  id: "operations.pendingApprovals", module: "operations", title: "Aprovações pendentes",
  description: "Timesheets submetidos aguardando aprovação do gestor.", size: "1x1", category: "kpi", icon: AlertCircle,
  Component: () => (
    <KpiWidget tone="amber" title="Aprovações pendentes" endpoint="/operations/dashboard/pending-approvals"
               icon={AlertCircle} href="/timesheet" />
  ),
});

registerWidget({
  id: "operations.activeSquads", module: "operations", title: "Squads ativos",
  description: "Total de squads de clientes em operação.", size: "1x1", category: "kpi", icon: Layers,
  Component: () => (
    <KpiWidget tone="ink" title="Squads ativos" endpoint="/operations/dashboard/active-squads"
               icon={Layers} href="/squads" />
  ),
});

registerWidget({
  id: "operations.utilization", module: "operations", title: "Ocupação",
  description: "Percentual de profissionais ativos com alocação vigente.", size: "1x1", category: "kpi", icon: BarChart2,
  Component: () => (
    <KpiWidget tone="blue" title="Ocupação" endpoint="/operations/dashboard/utilization"
               icon={BarChart2} href="/alocacoes" valueFormatter={fmtPct} />
  ),
});

registerWidget({
  id: "operations.expectedRevenue", module: "operations", title: "Receita prevista",
  description: "Soma do valor mensal das alocações ativas.", size: "1x1", category: "kpi", icon: DollarSign,
  Component: () => (
    <KpiWidget tone="green" title="Receita prevista" endpoint="/operations/dashboard/expected-revenue"
               icon={DollarSign} href="/alocacoes" valueFormatter={fmtBRL} />
  ),
});

registerWidget({
  id: "operations.allocationsEndingSoon", module: "operations", title: "Alocações encerrando (30d)",
  description: "Alocações com término nos próximos 30 dias.", size: "1x1", category: "kpi", icon: CalendarOff,
  Component: () => (
    <KpiWidget tone="red" title="Alocações encerrando (30d)" endpoint="/operations/dashboard/allocations-ending-soon"
               icon={CalendarOff} href="/alocacoes" />
  ),
});

// ── List registrations ───────────────────────────────────────────────────────

type EndingSoonItem = {
  id: string;
  professional_id?: string;
  professional_name: string;
  client_name: string;
  end_date: string;
  days_left: number;
};
registerWidget({
  id: "operations.allocationsEndingSoonList", module: "operations", title: "Alocações encerrando (lista)",
  description: "Lista das alocações com término mais próximo.", size: "2x1", category: "list", icon: CalendarOff,
  Component: () => (
    <ListWidget<EndingSoonItem>
      title="Alocações encerrando (30d)"
      endpoint="/operations/dashboard/allocations-ending-soon-list"
      icon={CalendarOff}
      tone="red"
      seeAllHref="/alocacoes"
      renderItem={(a) => (
        <Link to={`/alocacoes/${a.id}`} className="block hover:text-[var(--ink)]">
          <span className="flex items-center justify-between text-sm">
            <span>
              {a.professional_name}
              {a.client_name && <span className="text-muted-foreground"> · {a.client_name}</span>}
            </span>
            <span className="text-danger">{a.days_left}d</span>
          </span>
        </Link>
      )}
    />
  ),
});

type PendingTimesheetItem = {
  id: string;
  professional_id?: string;
  professional_name: string;
  entry_date: string;
  hours_worked: number;
};
registerWidget({
  id: "operations.pendingTimesheets", module: "operations", title: "Timesheets pendentes",
  description: "Registros de horas submetidos aguardando aprovação.", size: "2x1", category: "list", icon: Timer,
  Component: () => (
    <ListWidget<PendingTimesheetItem>
      title="Timesheets pendentes"
      endpoint="/operations/dashboard/pending-timesheets"
      icon={Timer}
      tone="amber"
      seeAllHref="/timesheet/all-times"
      renderItem={(t) => (
        <Link to="/timesheet/all-times" className="block hover:text-[var(--ink)]">
          <span className="flex items-center justify-between text-sm">
            <span>
              {t.professional_name}
              <span className="text-muted-foreground"> · {t.entry_date}</span>
            </span>
            <span className="text-muted-foreground">{t.hours_worked}h</span>
          </span>
        </Link>
      )}
    />
  ),
});

type NoHoursPro = { id: string; name: string };
registerWidget({
  id: "operations.professionalsNoHours", module: "operations", title: "Pessoas sem horas no mês",
  description: "Profissionais ativos sem nenhum registro de horas no mês corrente.", size: "2x1", category: "list", icon: UserX,
  Component: () => (
    <ListWidget<NoHoursPro>
      title="Pessoas sem horas no mês"
      endpoint="/operations/dashboard/professionals-no-hours"
      icon={UserX}
      tone="red"
      seeAllHref="/timesheet"
      renderItem={(p) => (
        <Link to={`/profissionais/${p.id}`} className="block hover:text-[var(--ink)]">
          <span className="text-sm">{p.name}</span>
        </Link>
      )}
    />
  ),
});

type LateProject = {
  id: string;
  name: string;
  client_name: string;
  days_late: number;
};
registerWidget({
  id: "operations.lateProjects", module: "operations", title: "Projetos atrasados",
  description: "Projetos com prazo vencido e ainda não concluídos.", size: "2x1", category: "list", icon: AlertTriangle,
  Component: () => (
    <ListWidget<LateProject>
      title="Projetos atrasados"
      endpoint="/operations/dashboard/late-projects"
      icon={AlertTriangle}
      tone="red"
      seeAllHref="/projetos"
      renderItem={(p) => (
        <Link to={`/projetos/${p.id}`} className="block hover:text-[var(--ink)]">
          <span className="flex items-center justify-between text-sm">
            <span>
              {p.name}
              {p.client_name && <span className="text-muted-foreground"> · {p.client_name}</span>}
            </span>
            <span className="text-danger">{p.days_late}d</span>
          </span>
        </Link>
      )}
    />
  ),
});

// ── Chart registrations ──────────────────────────────────────────────────────

registerWidget({
  id: "operations.hoursTrend6m", module: "operations", title: "Horas (6 meses)",
  description: "Evolução das horas registradas nos últimos 6 meses.",
  size: "2x1", category: "chart", icon: Clock,
  Component: HoursTrendChart,
});

registerWidget({
  id: "operations.allocationsByStatus", module: "operations", title: "Alocações por status",
  description: "Distribuição das alocações por status atual.",
  size: "2x1", category: "chart", icon: Layers,
  Component: AllocationsByStatusChart,
});
